/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useDispatch, useSelector } from 'react-redux'
import { Dispatch } from '@reduxjs/toolkit'
import { useAuth } from '../modules/auth'
import { useWebSocketHook } from './index'
import { createCard, getHomeColumnCounts } from '../services/cards'
import { getBoardPersonById } from '../services/boards'
import { countCardNumbers, findAndAddCardPosition } from '../helper-functions/findCardNumAndPos'
import { webSocketOperations } from '../constants'
import { capitalizeEachWord } from '../helper-functions/CapitalizeName'
import { setIsCardUpdated } from '../store/slice/last-update-slice'
import { fetchAllCardsofLoggedInUser, fetchAllCardByBoardId } from '../store/slice/card-slice'
import { setIsRender } from '../store/slice/isRender-slice'

// Minimal checklist assignee shape duplicated here to avoid a circular import
// between TextEditor.tsx and this hook. `_bpid` is the board_person_id
// stamped at pick time by ChecklistAssigneeDropdown — when present we use it
// directly so the new task lands with the right assignees even if the
// runtime userId → bpid lookup misses (network failure, stale roster, etc).
interface ChecklistAssigneeLike {
  id: string
  name: string
  source: 'board' | 'contact' | 'freetext'
  _bpid?: string
}

interface Params {
  cardDetails: any
}

export function useCreateTaskFromChecklist({ cardDetails }: Params) {
  const dispatch: Dispatch<any> = useDispatch()
  const { currentUser } = useAuth()
  const { id: userId, fullName } = currentUser?.data?.user || {}
  const { allBoards } = useSelector((state: any) => state.allBoardsReducer)
  const isLastUpdated = useSelector((state: any) => state.lastUpdateReducer)
  const { isCardUpdated } = isLastUpdated?.isCardUpdated ?? { isCardUpdated: false }
  const isRenderStore = useSelector((state: any) => state.isRenderReducer)
  const { isRender } = isRenderStore?.isRender ?? { isRender: false }
  const { sendJsonMessage }: any = useWebSocketHook()

  return async ({
    title,
    assignees,
    due_date = null,
    due_time = null,
  }: {
    title: string
    assignees: ChecklistAssigneeLike[]
    due_date?: string | null
    due_time?: string | null
  }) => {
    const boardId = cardDetails?.boardId
    const columnId = cardDetails?.board_column_id
    if (!boardId || !columnId) {
      throw new Error('Missing board or column context')
    }

    // Bottom-of-column position — "down" per spec. If anything about the
    // count lookup fails, fall back to 0 and let the backend sort it out.
    let position = 1
    try {
      const { cardLength } = await countCardNumbers(boardId, userId)
      position = findAndAddCardPosition(cardLength, 'down') ?? 1
    } catch {
      /* fall through */
    }

    let home_position = position
    try {
      const countsRes = await getHomeColumnCounts()
      const counts = countsRes.data.data
      const columnData = counts?.['todo']
      home_position = columnData && columnData.count > 0 ? columnData.max + 1 : 1
    } catch {
      /* fall through */
    }

    // Resolve checklist-item assignees to board_person_id. The
    // ChecklistAssignee.id for source==='board' is the *userId*; the
    // backend's card_persons FK expects the board-person join-table id.
    // Always run the lookup so the creator fallback below has a working
    // userId → bpid map even when every assignee already carries _bpid.
    // The stamped _bpid (when present) is still preferred for the board
    // assignees themselves so a failed/partial fetch can't drop them.
    let userIdToBoardPersonId: Record<string, string> = {}
    try {
      const res = await getBoardPersonById(boardId)
      const persons = res?.data?.data ?? []
      userIdToBoardPersonId = persons.reduce((acc: Record<string, string>, p: any) => {
        if (p?.userId && p?.id) acc[p.userId] = p.id
        return acc
      }, {})
    } catch {
      /* fall through — we'll fall back to the creator below */
    }

    const boardAssignees = assignees
      .filter((a) => a.source === 'board')
      .map((a) => a._bpid ?? userIdToBoardPersonId[a.id])
      .filter((id): id is string => Boolean(id))
      .map((id) => ({ board_person_id: id }))

    const creatorBoardPersonId = userId ? userIdToBoardPersonId[userId] : undefined
    const creatorFallback = creatorBoardPersonId
      ? [{ board_person_id: creatorBoardPersonId }]
      : []
    const payloadAssignees = boardAssignees.length > 0 ? boardAssignees : creatorFallback

    const boardName =
      allBoards?.find((item: any) => item?.board?.id === boardId)?.board?.name?.trim() ?? ''

    const postData = {
      created_by: fullName,
      last_modified_by: fullName,
      board_name: boardName,
      card_name: title,
      description: null,
      due_date,
      due_time,
      position,
      home_position,
      priority: null,
      rag: null,
      status: 'todo',
      version: 1,
      applicationId: null,
      boardId,
      board_column_id: columnId,
      categoryId: null,
      componentId: null,
      customerId: null,
      productId: null,
      typeId: null,
      reporter: fullName,
      cardType: 'individual',
      organizationId: null,
      tagIds: [],
      assignees: payloadAssignees,
      file_attachments: [],
      reminders: [],
    }

    const res = await createCard(postData, userId)
    const newCard = res.data.data

    // Build the enriched assignees the caller will stamp on the DOM —
    // {board_person_id, fullName, userId} triples covering everyone the
    // new task was created with. The userId field lets onAssigneeOpen
    // match against the board roster by id (boardMembers[i].id IS
    // userId) which is more reliable than name matching when auth
    // and board APIs disagree on fullName format.
    const boardAssigneeNames = new Map<string, string>()
    const boardPersonIdToUserId: Record<string, string> = {}
    assignees.forEach((a) => {
      if (a.source === 'board') {
        const bpid = a._bpid ?? userIdToBoardPersonId[a.id]
        if (bpid) {
          boardAssigneeNames.set(bpid, a.name)
          boardPersonIdToUserId[bpid] = a.id
        }
      }
    })
    Object.entries(userIdToBoardPersonId).forEach(([uid, bpid]) => {
      if (!boardPersonIdToUserId[bpid]) boardPersonIdToUserId[bpid] = uid
    })
    const enrichedAssignees: Array<{
      board_person_id: string
      fullName: string
      userId?: string
    }> = payloadAssignees.map((p) => ({
      board_person_id: p.board_person_id,
      fullName:
        boardAssigneeNames.get(p.board_person_id) ??
        (p.board_person_id === creatorBoardPersonId ? (fullName ?? '') : ''),
      userId: boardPersonIdToUserId[p.board_person_id],
    }))

    sendJsonMessage({
      event: 'publish',
      channel: boardId,
      userId,
      command: 'CREATE',
      operation: webSocketOperations.cardCreate,
      payload: {
        newCard,
        boardId,
        alert: `${capitalizeEachWord(fullName)} created a new card ${newCard.board_name}- ${newCard.number}.`,
      },
    })

    dispatch(setIsCardUpdated({ isCardUpdated: !isCardUpdated }))
    dispatch(fetchAllCardsofLoggedInUser())
    // Refetch the board so the new task shows up in BoardDnD / Trello
    // columns without a manual reload. The parent card modal remains open
    // because EditBoardCard's `checkAssignee` match now handles the object
    // shape of `updatedCard.assignees` (see EditBoardCard.tsx).
    dispatch(fetchAllCardByBoardId({ boardId, userId }))
    dispatch(setIsRender({ isRender: !isRender }))

    return { id: newCard.id, key: newCard.key, assignees: enrichedAssignees }
  }
}
